// src/components/admin/PanelTestAliados.js
// Panel de gestión de aliados de prueba. Solo SuperAdmin.
// Va colgado en el tab Integraciones (junto al panel test-afiliados y config canje).

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Alert, Button, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, IconButton, Tooltip,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  getRangoAliadosTest, listarTestAliados,
  crearBatchAliadosTest, limpiarTodoAliadosTest,
} from '../../services/testAliadosApi';

export default function PanelTestAliados({ onToast }) {
  const [rango, setRango]         = useState(null);
  const [aliados, setAliados]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [dlgCrear, setDlgCrear]   = useState({ open: false, cantidad: 5 });
  const [dlgLimpiar, setDlgLimpiar] = useState({ open: false, confirmacion: '' });
  const [enCurso, setEnCurso]     = useState(false);
  const [ultimoBatch, setUltimoBatch] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [r, l] = await Promise.all([getRangoAliadosTest(), listarTestAliados()]);
      setRango(r);
      setAliados(l.aliados || []);
    } catch (e) {
      onToast?.(e.message || 'No se pudo cargar.', 'error');
    } finally { setLoading(false); }
  }, [onToast]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleCrearBatch = async () => {
    setEnCurso(true);
    try {
      const r = await crearBatchAliadosTest({
        cantidad: parseInt(dlgCrear.cantidad, 10),
      });
      onToast?.(r.mensaje || 'Batch creado.', 'success');
      setUltimoBatch(r);
      setDlgCrear({ open: false, cantidad: 5 });
      await cargar();
    } catch (e) {
      onToast?.(e.message || 'Error al crear batch.', 'error');
    } finally { setEnCurso(false); }
  };

  const handleLimpiar = async () => {
    if (dlgLimpiar.confirmacion !== 'LIMPIAR') {
      onToast?.('Debe escribir LIMPIAR para confirmar.', 'warning');
      return;
    }
    setEnCurso(true);
    try {
      const r = await limpiarTodoAliadosTest();
      onToast?.(
        `${r.aliadosEliminados} aliados y ${r.documentosEliminados} documento(s) eliminados.`,
        'success'
      );
      setDlgLimpiar({ open: false, confirmacion: '' });
      setUltimoBatch(null);
      await cargar();
    } catch (e) {
      onToast?.(e.message || 'Error al limpiar.', 'error');
    } finally { setEnCurso(false); }
  };

  const copiar = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => onToast?.(`Copiado: ${text}`, 'success'))
      .catch(() => onToast?.('No se pudo copiar.', 'error'));
  };

  const cupoUsado = aliados.length;
  const cupoTotal = rango?.nitCupo || 100;
  const cupoDisponible = cupoTotal - cupoUsado;

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3, border: '2px dashed #6b8e6b', bgcolor: '#f7fbf7' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <StorefrontIcon sx={{ color: '#6b8e6b' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#2f5233' }}>
            Aliados de prueba
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Mecanismo secreto de test — solo SuperAdmin
          </Typography>
        </Box>
        <Tooltip title="Recargar">
          <IconButton onClick={cargar} disabled={loading}><RefreshIcon /></IconButton>
        </Tooltip>
      </Stack>

      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Rangos reservados: NIT <code>{rango?.nitRangoInicio?.toLocaleString()}</code> a{' '}
          <code>{rango?.nitRangoFin?.toLocaleString()}</code> · Cédula representante{' '}
          <code>{rango?.cedulaRepRangoInicio?.toLocaleString()}</code> a{' '}
          <code>{rango?.cedulaRepRangoFin?.toLocaleString()}</code>
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Los NITs y cédulas en estos rangos <strong>saltan la verificación OCR automáticamente</strong>. Todos los
          aliados se crean en estado <code>PENDIENTE_APROBACION</code> para permitir probar el flujo completo
          (aprobación, activación en marketplace/POS, rechazo, suspensión). Luego los borras en batch sin afectar
          datos reales.
        </Typography>
      </Alert>

      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <Chip label={`${cupoUsado} / ${cupoTotal} usados`}
          color={cupoUsado === 0 ? 'default' : cupoDisponible > 20 ? 'success' : cupoDisponible > 0 ? 'warning' : 'error'}
          sx={{ fontWeight: 700 }} />
        <Button variant="contained" color="warning" startIcon={<AddIcon />}
          onClick={() => setDlgCrear({ open: true, cantidad: 5 })}
          disabled={cupoDisponible === 0}>
          Crear batch
        </Button>
        <Button variant="outlined" color="error" startIcon={<DeleteForeverIcon />}
          onClick={() => setDlgLimpiar({ open: true, confirmacion: '' })}
          disabled={cupoUsado === 0}>
          Limpiar todo
        </Button>
      </Stack>

      {ultimoBatch && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUltimoBatch(null)}>
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Último batch creado ({ultimoBatch.cantidadCreada} aliados):
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0, fontSize: '0.82rem' }}>
            {ultimoBatch.aliados?.map((a) => (
              <li key={a.aliadoID}>
                <code>{a.codigo}</code> — {a.razonSocial} — NIT <code>{a.nit}</code> — Rep. {a.representanteLegal} (<code>{a.cedulaRepresentante}</code>)
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {loading && <CircularProgress size={20} />}

      {aliados.length > 0 && (
        <TableContainer variant="outlined" component={Paper}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>NIT</TableCell>
                <TableCell>Razón Social</TableCell>
                <TableCell>Representante</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Creado</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {aliados.map(a => (
                <TableRow key={a.aliadoID}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{a.codigo}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {a.nit}
                    <Chip label="TEST" size="small" color="warning" sx={{ ml: 1, fontSize: '0.6rem', height: 16 }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.82rem' }}>
                    {a.razonSocial}
                    {a.nombreComercial && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {a.nombreComercial}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.78rem' }}>
                    {a.representanteLegal}
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {a.cedulaRepresentante}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>
                    {a.email}
                    <Tooltip title="Copiar email">
                      <IconButton size="small" onClick={() => copiar(a.email)}>
                        <ContentCopyIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip label={a.estadoAprobacion} size="small" sx={{ fontSize: '0.65rem' }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.72rem' }}>
                    {a.fechaCreacion ? new Date(a.fechaCreacion).toLocaleDateString('es-CO') : '—'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Copiar NIT">
                      <IconButton size="small" onClick={() => copiar(a.nit)}>
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog crear */}
      <Dialog open={dlgCrear.open} onClose={() => setDlgCrear({ ...dlgCrear, open: false })} maxWidth="xs" fullWidth>
        <DialogTitle>Crear aliados de prueba</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Crearemos aliados con NIT del rango reservado, cédula del representante del rango test, y estado
            inicial <code>PENDIENTE_APROBACION</code> para probar todo el flujo desde el principio.
          </Alert>
          <Stack spacing={2}>
            <TextField label="Cantidad (1-20)" type="number" fullWidth
              value={dlgCrear.cantidad}
              onChange={e => setDlgCrear(p => ({ ...p, cantidad: e.target.value }))}
              inputProps={{ min: 1, max: 20 }} />
            <Typography variant="caption" color="text.secondary">
              El tipo de negocio y el municipio se toman por defecto (el primer TipoNegocio activo y el primer
              Municipio de la BD). Si necesitas específicos, dime.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgCrear({ ...dlgCrear, open: false })} disabled={enCurso}>Cancelar</Button>
          <Button variant="contained" color="warning" onClick={handleCrearBatch}
            disabled={enCurso || !dlgCrear.cantidad || parseInt(dlgCrear.cantidad, 10) < 1}
            startIcon={enCurso ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}>
            Crear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog limpiar */}
      <Dialog open={dlgLimpiar.open} onClose={() => setDlgLimpiar({ open: false, confirmacion: '' })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>⚠️ Limpiar TODOS los aliados de prueba</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
              Esta acción es IRREVERSIBLE.
            </Typography>
            <Typography variant="body2">
              Se eliminarán en cascada:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 0.5 }}>
              <li>{cupoUsado} aliado(s) del rango test (NIT 999xxxxxxx)</li>
              <li>Todos sus documentos adjuntos (RUT, cámara, etc.)</li>
            </Box>
          </Alert>
          <TextField
            label="Escribe LIMPIAR para confirmar"
            fullWidth
            value={dlgLimpiar.confirmacion}
            onChange={e => setDlgLimpiar(p => ({ ...p, confirmacion: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgLimpiar({ open: false, confirmacion: '' })} disabled={enCurso}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleLimpiar}
            disabled={enCurso || dlgLimpiar.confirmacion !== 'LIMPIAR'}
            startIcon={enCurso ? <CircularProgress size={14} color="inherit" /> : <DeleteForeverIcon />}>
            Limpiar todo
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
